import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { TextClassContext } from '@/ui/core/text'
import type { TextRef } from '@/ui/primitives/types'
import { cn } from '@/ui/utils'
import { forwardRef } from 'react'
import type { ElementRef, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { Text as RNText } from 'react-native'
import { buttonStyles, buttonTextStyles } from './button.styles'
import type {
	ButtonProps,
	ButtonTextProps,
	ButtonVariant,
	DisabledVariant,
	SizeVariant,
} from './button.types'

type ButtonContext = {
	variant: ButtonVariant
	size: SizeVariant
	disabled: DisabledVariant
	selected: boolean
}

/** The spinner takes the label's colour, so a busy button reads as the same button. */
const VariantToButtonLoaderMap: Record<NonNullable<ButtonVariant>, string> = {
	primary: colors.white,
	accent: colors.white,
	secondary: colors.ink,
	link: colors.ink,
	ground: colors.ink,
	ghost: colors.white,
	blush: colors.ink,
}

const ButtonContext = createContext<ButtonContext>({
	variant: 'primary',
	size: 'default',
	disabled: false,
	selected: false,
})

const Button = forwardRef<ElementRef<typeof Pressable>, ButtonProps>(
	(
		{
			className,
			variant = 'primary',
			size = 'default',
			fullWidth = false,
			selected = false,
			disabled = false,
			children,
			isLoading = false,
			loadingLabel,
			...props
		},
		ref
	) => {
		// A working button is not an inactive one. While it is busy it keeps its filled look, so
		// the spinner has a ground to read against; it just cannot be pressed again.
		const inactive = disabled && !isLoading
		return (
			<TextClassContext.Provider
				value={cn(buttonTextStyles({ variant, size, selected, disabled: inactive }))}
			>
				<ButtonContext.Provider value={{ variant, size, disabled: inactive, selected }}>
					<Pressable
						ref={ref}
						role="button"
						disabled={disabled || isLoading}
						accessibilityState={{ disabled: disabled || isLoading, busy: isLoading }}
						// The 28pt chip keeps its look and gets a 44pt target.
						hitSlop={size === 'sm' ? 8 : undefined}
						className={cn(
							buttonStyles({ variant, size, fullWidth, selected, disabled: inactive }),
							className
						)}
						{...props}
					>
						{isLoading && (
							<View
								className="absolute left-0 right-0 flex-row items-center justify-center"
								style={{ gap: 8 }}
							>
								<ActivityIndicator
									color={VariantToButtonLoaderMap[variant ?? 'primary']}
								/>
								{!!loadingLabel && <ButtonText>{loadingLabel}</ButtonText>}
							</View>
						)}
						{/* The label keeps its space so the button does not resize, but steps aside
					    for the spinner rather than sitting under it. */}
						<View style={isLoading ? { opacity: 0 } : undefined}>
							{children as ReactNode}
						</View>
					</Pressable>
				</ButtonContext.Provider>
			</TextClassContext.Provider>
		)
	}
)

const ButtonText = forwardRef<TextRef, ButtonTextProps>(
	({ className, children, spacing, ...props }, ref) => {
		const { variant, size, disabled, selected } = useContext(ButtonContext)

		return (
			<RNText
				maxFontSizeMultiplier={1.4}
				ref={ref}
				className={cn(
					buttonTextStyles({ variant, size, disabled, selected, spacing }),
					className
				)}
				{...props}
			>
				{children}
			</RNText>
		)
	}
)

export { Button, ButtonText }
